import { useMemo } from 'react';
import styled from 'styled-components';

import { cA568FE } from '#/colors';

import { Loadable } from '#/components/shared/Loadable';

import { useOrdersView } from '#/features/perpetuals/trade-overview/orders-context';

import maskString from '#/utils/maskString';
import copyToClipboard from '#/utils/copyToClipboard';

import { createTradeOverviewTable, TableSpec } from './table';
import { Empty, Error, SideBuy, SideSell } from './atoms';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function OrderHistory(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, error, orders } = useOrdersView();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = orders.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>You have no orders</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {orders.map((order) => (
          <Row
            key={order.id}
            time={
              <span css="font-size: 11px;">
                <span>{order.createdTime}</span>
                <br />
                <span css="opacity: 0.7">{order.createdDate}</span>
              </span>
            }
            market={order.market}
            type={order.type}
            instruction={order.instruction}
            side={sideComponent[order.side]}
            price={order.price}
            amount={order.size}
            remainingSize={order.remainingSize}
            status={order.status}
            cancelReason={order.cancelReason}
            orderId={
              <Id onClick={() => copyToClipboard(order.id)}>
                {maskString(order.id, 4, 4)}
              </Id>
            }
            clientId={
              <Id
                title={order.clientId}
                onClick={() => copyToClipboard(order.clientId)}
              >
                {maskString(order.clientId, 6, 6)}
              </Id>
            }
          />
        ))}
      </Table>
    </Loadable>
  );
}

const sideComponent = {
  BUY: <SideBuy />,
  SELL: <SideSell />,
};

const Id = styled.div`
  &:hover {
    cursor: pointer;
    color: ${cA568FE};
  }
`;
